
import {
  DocumentTemplate,
  DocumentTemplateWithEventName,
} from "@/types/DocumentTemplate";
import {
  getDocumentTemplates as getDocumentTemplatesApi,
  getDocumentTemplatesByUserPaginated as getDocumentTemplatesByUserPaginatedApi,
  getDocumentTemplate as getDocumentTemplateApi,
  createDocumentTemplate as createDocumentTemplateApi,
  updateDocumentTemplate as updateDocumentTemplateApi,
  deleteDocumentTemplate as deleteDocumentTemplateApi,
  bulkDeleteDocumentTemplates as bulkDeleteDocumentTemplatesApi,
  duplicateDocumentTemplate as duplicateDocumentTemplateApi,
  bulkDuplicateDocumentTemplates as bulkDuplicateDocumentTemplatesApi,
  downloadDocumentPdf as downloadDocumentPdfApi,
  downloadDocumentPdfForGuest as downloadDocumentPdfForGuestApi,
  downloadDocumentPdfBatch as downloadDocumentPdfBatchApi,
  presignDocumentFile as presignDocumentFileApi,
  confirmDocumentFile as confirmDocumentFileApi,
  deleteDocumentFile as deleteDocumentFileApi,
} from "@/lib/api/clients/DocumentTemplateClient";
import {
  getTemplateDocumentTemplates as getTemplateDocumentTemplatesApi,
  getTemplateDocumentTemplate as getTemplateDocumentTemplateApi,
  createTemplateDocumentTemplate as createTemplateDocumentTemplateApi,
  updateTemplateDocumentTemplate as updateTemplateDocumentTemplateApi,
  deleteTemplateDocumentTemplate as deleteTemplateDocumentTemplateApi,
  duplicateTemplateDocumentTemplate as duplicateTemplateDocumentTemplateApi,
  downloadTemplateDocumentPdf as downloadTemplateDocumentPdfApi,
  presignTemplateDocumentFile as presignTemplateDocumentFileApi,
  confirmTemplateDocumentFile as confirmTemplateDocumentFileApi,
  deleteTemplateDocumentFile as deleteTemplateDocumentFileApi,
} from "@/lib/api/clients/TemplateDocumentClient";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import { isValidObjectId } from "@/lib/utils/input-validator";

export async function getDocumentTemplatesHandler(
  eventId: string,
): Promise<DocumentTemplate[] | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    return await getDocumentTemplatesApi(eventId);
  } catch (error) {
    console.error("Error getting document templates:", error);
    return handleErrorServer(error);
  }
}

/**
 * Saved STANDARD templates across the caller's events for the creation
 * wizard's import tabs. Exactly one of eventId / excludeEventId is set.
 */
export async function getDocumentTemplatesByUserPaginatedHandler(params: {
  eventId?: string;
  excludeEventId?: string;
  documentType?: string;
  fields: string;
  page?: number;
  pageSize?: number;
  search?: string;
}): Promise<DocumentTemplateWithEventName[] | ServerError> {
  try {
    if (params.eventId && !isValidObjectId(params.eventId))
      throw new Error("validationError");
    if (params.excludeEventId && !isValidObjectId(params.excludeEventId))
      throw new Error("validationError");
    return await getDocumentTemplatesByUserPaginatedApi(params);
  } catch (error) {
    console.error("Error getting document templates by user:", error);
    return handleErrorServer(error);
  }
}

export async function getDocumentTemplateHandler(
  eventId: string,
  documentTemplateId: string,
): Promise<DocumentTemplate | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!isValidObjectId(documentTemplateId)) throw new Error("validationError");
    return await getDocumentTemplateApi(eventId, documentTemplateId);
  } catch (error) {
    console.error("Error getting document template:", error);
    return handleErrorServer(error);
  }
}

export async function createDocumentTemplateHandler(
  eventId: string,
  template: Partial<DocumentTemplate>,
): Promise<string | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    return await createDocumentTemplateApi(eventId, template);
  } catch (error) {
    console.error("Error creating document template:", error);
    return handleErrorServer(error);
  }
}

export async function updateDocumentTemplateHandler(
  eventId: string,
  documentTemplateId: string,
  template: Partial<DocumentTemplate>,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!isValidObjectId(documentTemplateId)) throw new Error("validationError");
    if (!template || Object.keys(template).length === 0) throw new Error("validationError");
    return await updateDocumentTemplateApi(eventId, documentTemplateId, template);
  } catch (error) {
    console.error("Error updating document template:", error);
    return handleErrorServer(error);
  }
}

export async function deleteDocumentTemplateHandler(
  eventId: string,
  documentTemplateId: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!isValidObjectId(documentTemplateId)) throw new Error("validationError");
    return await deleteDocumentTemplateApi(eventId, documentTemplateId);
  } catch (error) {
    console.error("Error deleting document template:", error);
    return handleErrorServer(error);
  }
}

export async function bulkDeleteDocumentTemplatesHandler(
  eventId: string,
  documentTemplateIds: string[],
): Promise<void | ServerError> {
  if (!isValidObjectId(eventId)) return handleErrorServer("invalidEventId");
  try {
    await bulkDeleteDocumentTemplatesApi(eventId, documentTemplateIds);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function bulkDuplicateDocumentTemplatesHandler(
  eventId: string,
  documentTemplateIds: string[],
): Promise<DocumentTemplate[] | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    return await bulkDuplicateDocumentTemplatesApi(eventId, documentTemplateIds);
  } catch (error) {
    console.error("Error bulk duplicating document templates:", error);
    return handleErrorServer(error);
  }
}

export async function duplicateDocumentTemplateHandler(
  eventId: string,
  documentTemplateId: string,
): Promise<string | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!isValidObjectId(documentTemplateId)) throw new Error("validationError");
    return await duplicateDocumentTemplateApi(eventId, documentTemplateId);
  } catch (error) {
    console.error("Error duplicating document template:", error);
    return handleErrorServer(error);
  }
}

export async function presignDocumentFileHandler(
  eventId: string,
  documentTemplateId: string,
  data: { fileName: string; contentType: string; fileSize: number },
): Promise<{ uploadUrl: string; s3Key: string; fileId: string } | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!isValidObjectId(documentTemplateId)) throw new Error("validationError");
    return await presignDocumentFileApi(eventId, documentTemplateId, data);
  } catch (error) {
    console.error("Error presigning document file:", error);
    return handleErrorServer(error);
  }
}

export async function confirmDocumentFileHandler(
  eventId: string,
  documentTemplateId: string,
  data: {
    s3Key: string;
    fileName: string;
    fileSize: number;
    contentType: string;
    targetField: string;
  },
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!isValidObjectId(documentTemplateId)) throw new Error("validationError");
    return await confirmDocumentFileApi(eventId, documentTemplateId, data);
  } catch (error) {
    console.error("Error confirming document file:", error);
    return handleErrorServer(error);
  }
}

export async function deleteDocumentFileHandler(
  eventId: string,
  documentTemplateId: string,
  fileId: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!isValidObjectId(documentTemplateId)) throw new Error("validationError");
    return await deleteDocumentFileApi(eventId, documentTemplateId, fileId);
  } catch (error) {
    console.error("Error deleting document file:", error);
    return handleErrorServer(error);
  }
}

export async function downloadDocumentTemplatePdf(
  eventId: string,
  documentTemplateId: string,
  htmlPages: string[],
  widthPx: number,
  heightPx: number,
  fileName: string,
  /**
   * Art. 50 Abs. 2 KI-VO (plan A7/D2b): the open, UNSAVED "document" AI mark
   * of the builder these pages come from — forwarded as the write-only body
   * field. It can only add the PDF's AI-Disclosure, never remove one.
   */
  aiContentTool?: string,
): Promise<Uint8Array | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!isValidObjectId(documentTemplateId)) throw new Error("validationError");
    return await downloadDocumentPdfApi(eventId, documentTemplateId, htmlPages, widthPx, heightPx, fileName, aiContentTool);
  } catch (error) {
    console.error("Error downloading document PDF:", error);
    return handleErrorServer(error);
  }
}

export async function downloadDocumentTemplatePdfForGuest(
  eventId: string,
  documentTemplateId: string,
  guestId: string,
  htmlPages: string[],
  widthPx: number,
  heightPx: number,
  fileName: string,
  /** Art. 50 KI-VO: see `downloadDocumentTemplatePdf`. */
  aiContentTool?: string,
): Promise<Uint8Array | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!isValidObjectId(documentTemplateId)) throw new Error("validationError");
    if (!isValidObjectId(guestId)) throw new Error("validationError");
    return await downloadDocumentPdfForGuestApi(eventId, documentTemplateId, guestId, htmlPages, widthPx, heightPx, fileName, aiContentTool);
  } catch (error) {
    console.error("Error downloading per-guest document PDF:", error);
    return handleErrorServer(error);
  }
}

export async function downloadDocumentTemplatePdfBatch(
  eventId: string,
  documentTemplateId: string,
  guestIds: string[],
  htmlPages: string[],
  widthPx: number,
  heightPx: number,
  fileName: string,
  /** Art. 50 KI-VO: see `downloadDocumentTemplatePdf`. */
  aiContentTool?: string,
): Promise<Uint8Array | ServerError> {
  try {
    if (!isValidObjectId(eventId)) throw new Error("validationError");
    if (!isValidObjectId(documentTemplateId)) throw new Error("validationError");
    if (guestIds.length === 0 || guestIds.some((id) => !isValidObjectId(id))) {
      throw new Error("validationError");
    }
    return await downloadDocumentPdfBatchApi(eventId, documentTemplateId, guestIds, htmlPages, widthPx, heightPx, fileName, aiContentTool);
  } catch (error) {
    console.error("Error downloading batch document PDF:", error);
    return handleErrorServer(error);
  }
}

// ── Template-aware handlers (templateId context) ──

export async function getTemplateDocumentTemplatesHandler(
  templateId: string,
): Promise<DocumentTemplate[] | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    return await getTemplateDocumentTemplatesApi(templateId);
  } catch (error) {
    console.error("Error getting template document templates:", error);
    return handleErrorServer(error);
  }
}

export async function getTemplateDocumentTemplateHandler(
  templateId: string,
  documentTemplateId: string,
): Promise<DocumentTemplate | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(documentTemplateId)) throw new Error("validationError");
    return await getTemplateDocumentTemplateApi(templateId, documentTemplateId);
  } catch (error) {
    console.error("Error getting template document template:", error);
    return handleErrorServer(error);
  }
}

export async function createTemplateDocumentTemplateHandler(
  templateId: string,
  template: Partial<DocumentTemplate>,
): Promise<string | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    return await createTemplateDocumentTemplateApi(templateId, template);
  } catch (error) {
    console.error("Error creating template document template:", error);
    return handleErrorServer(error);
  }
}

export async function updateTemplateDocumentTemplateHandler(
  templateId: string,
  documentTemplateId: string,
  template: Partial<DocumentTemplate>,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(documentTemplateId)) throw new Error("validationError");
    if (!template || Object.keys(template).length === 0) throw new Error("validationError");
    return await updateTemplateDocumentTemplateApi(templateId, documentTemplateId, template);
  } catch (error) {
    console.error("Error updating template document template:", error);
    return handleErrorServer(error);
  }
}

export async function deleteTemplateDocumentTemplateHandler(
  templateId: string,
  documentTemplateId: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(documentTemplateId)) throw new Error("validationError");
    return await deleteTemplateDocumentTemplateApi(templateId, documentTemplateId);
  } catch (error) {
    console.error("Error deleting template document template:", error);
    return handleErrorServer(error);
  }
}

export async function duplicateTemplateDocumentTemplateHandler(
  templateId: string,
  documentTemplateId: string,
): Promise<string | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(documentTemplateId)) throw new Error("validationError");
    return await duplicateTemplateDocumentTemplateApi(templateId, documentTemplateId);
  } catch (error) {
    console.error("Error duplicating template document template:", error);
    return handleErrorServer(error);
  }
}

export async function presignTemplateDocumentFileHandler(
  templateId: string,
  documentTemplateId: string,
  data: { fileName: string; contentType: string; fileSize: number },
): Promise<{ uploadUrl: string; s3Key: string; fileId: string } | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(documentTemplateId)) throw new Error("validationError");
    return await presignTemplateDocumentFileApi(templateId, documentTemplateId, data);
  } catch (error) {
    console.error("Error presigning template document file:", error);
    return handleErrorServer(error);
  }
}

export async function confirmTemplateDocumentFileHandler(
  templateId: string,
  documentTemplateId: string,
  data: {
    s3Key: string;
    fileName: string;
    fileSize: number;
    contentType: string;
    targetField: string;
  },
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(documentTemplateId)) throw new Error("validationError");
    return await confirmTemplateDocumentFileApi(templateId, documentTemplateId, data);
  } catch (error) {
    console.error("Error confirming template document file:", error);
    return handleErrorServer(error);
  }
}

export async function deleteTemplateDocumentFileHandler(
  templateId: string,
  documentTemplateId: string,
  fileId: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(documentTemplateId)) throw new Error("validationError");
    return await deleteTemplateDocumentFileApi(templateId, documentTemplateId, fileId);
  } catch (error) {
    console.error("Error deleting template document file:", error);
    return handleErrorServer(error);
  }
}

export async function downloadTemplateDocumentTemplatePdf(
  templateId: string,
  documentTemplateId: string,
  htmlPages: string[],
  widthPx: number,
  heightPx: number,
  fileName: string,
): Promise<Uint8Array | ServerError> {
  try {
    if (!isValidObjectId(templateId)) throw new Error("validationError");
    if (!isValidObjectId(documentTemplateId)) throw new Error("validationError");
    return await downloadTemplateDocumentPdfApi(templateId, documentTemplateId, htmlPages, widthPx, heightPx, fileName);
  } catch (error) {
    console.error("Error downloading template document PDF:", error);
    return handleErrorServer(error);
  }
}
